// src/app/components/agents/workflow-blocks.ts
//
// The workflow block vocabulary: the six blocks, their tones, and the labels
// on the connections between them.
//
// Source: handoff-advanced §3.1, which gives `BLOCK_DEFS` and
// `DEFAULT_EDGE_LABELS` verbatim.
//
// ── Why this is its own module ─────────────────────────────────────────────
//
// The list view previews each workflow AS ITS GRAPH, so the list and the
// builder canvas draw the same six blocks with the same six colours and the
// same connector wording. Two copies of that table is one table and one
// almost-table: the first block someone recolours in the builder is the first
// block that no longer matches its own preview.
//
// ── The six tones ─────────────────────────────────────────────────────────
//
// On a node graph the colour IS the categorical identity, so the six have to
// resolve to six visibly different hues. They were six generic Tailwind hex
// values — #0D9488, #7C3AED, #2563EB, #10B981, #F59E0B, #EF4444 — and every
// use of them faked its alpha by concatenating hex digits (`accentColor +
// "40"`). That works on a raw hex and breaks silently the moment the value
// becomes a `var()`, which is exactly what happened to the reporting module's
// `MODULE_COLORS`. So each block carries a TRIPLE plus an ink, all four out of
// the token file, and there is nothing left to concatenate.
//
// `download_report` is amber and `send_email` is coral, and in THIS token file
// those are genuinely different hues: `--st-warn-f` is #F5820A, `--a-gap` is
// #FF6D66. The handoff warns that in its source system the warning ramp is
// coral-based and the two would collapse. `advanced:check` resolves all six in
// the browser and fails on a collision, because the handoff's own instruction
// is to verify computed values rather than trust names.

import { BarChart2, Building2, Cpu, FileDown, Mail, MessageSquare } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type BlockType =
  | "brand_context"
  | "analysis_prompt"
  | "ai_models"
  | "perception_score"
  | "download_report"
  | "send_email";

export interface BlockDef {
  /** Title Case, as stored. The UI renders `blockLabel()`. */
  label: string;
  icon: LucideIcon;
  desc: string;
  category: "setup" | "output";
  /** The fill: the node's accent bar and its connection handles. */
  accent: string;
  /** The tinted ground behind the block's icon chip. */
  tint: string;
  /** The tinted edge. */
  line: string;
  /** The legible variant, for the icon and the category badge's ink. */
  ink: string;
}

export const WORKFLOW_BLOCKS: Record<BlockType, BlockDef> = {
  brand_context: {
    label: "Brand context", icon: Building2, desc: "Set the brand name to analyse", category: "setup",
    accent: "var(--a-brand)", tint: "var(--a-brand-sub)", line: "var(--a-brand-line)", ink: "var(--a-brand-t)",
  },
  analysis_prompt: {
    label: "Analysis prompt", icon: MessageSquare, desc: "The question for AI models", category: "setup",
    accent: "var(--a-reason)", tint: "var(--a-reason-sub)", line: "var(--a-reason-line)", ink: "var(--a-reason-t)",
  },
  ai_models: {
    label: "AI models", icon: Cpu, desc: "Select models to run against", category: "setup",
    accent: "var(--a-info)", tint: "var(--a-info-sub)", line: "var(--a-info-bd)", ink: "var(--a-info-t)",
  },
  perception_score: {
    label: "Perception score", icon: BarChart2, desc: "Generate a 0–100 perception score", category: "output",
    accent: "var(--st-ok-f)", tint: "var(--st-ok-bg)", line: "var(--st-ok-bd)", ink: "var(--st-ok-t)",
  },
  download_report: {
    label: "Download report", icon: FileDown, desc: "Export results as a JSON file", category: "output",
    accent: "var(--st-warn-f)", tint: "var(--st-warn-bg)", line: "var(--st-warn-bd)", ink: "var(--st-warn-t)",
  },
  send_email: {
    label: "Email results", icon: Mail, desc: "Send results to an email address", category: "output",
    accent: "var(--a-gap)", tint: "var(--a-gap-sub)", line: "var(--a-gap-line)", ink: "var(--a-gap-t)",
  },
};

export const SETUP_TYPES: BlockType[] = ["brand_context", "analysis_prompt", "ai_models"];
export const OUTPUT_TYPES: BlockType[] = ["perception_score", "download_report", "send_email"];

/**
 * The connector wording, §3.1's table verbatim.
 *
 * The fallback is "connects to" and it is deliberate: an edge the table has no
 * entry for is a connection somebody made by hand, and inventing a verb for it
 * would put words in their mouth.
 */
export const DEFAULT_EDGE_LABELS: Record<string, string> = {
  "brand_context→analysis_prompt": "defines brand for",
  "brand_context→ai_models": "context for",
  "analysis_prompt→ai_models": "runs on",
  "ai_models→perception_score": "generates",
  "ai_models→download_report": "exports to",
  "ai_models→send_email": "sends via",
};

export function edgeLabelFor(source: string, target: string): string {
  return DEFAULT_EDGE_LABELS[`${source}→${target}`] || "connects to";
}
